import { useContext, useState, useEffect } from 'react';
import { FC } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { SvgIcon, IconButton, Box } from '@mui/material';

import { HelpCircle } from 'react-feather';
import UserContext from 'src/contexts/UserContext';
import HtmlTooltip from 'src/components/HtmlTooltip';
import SupportModal, {
  OPEN_SUPPORT_MODAL_EVENT,
} from 'src/components/SupportModal';
import { getCompanyOnboardingState } from 'src/utils/featureUsage';
import {
  getOnboardingCardStateFromStorage,
  setOnboardingCardStateInStorage,
} from 'src/utils/onboardingCardStorage';

// Number of days after business creation during which the migration
// ("Onboarding Support") option is shown in the support modal. After this
// window the option is hidden.
const ONBOARDING_SUPPORT_ELIGIBLE_DAYS = 60;

const Contact: FC = () => {
  const userContext = useContext(UserContext).userData;
  const location = useLocation();
  const history = useHistory();

  const companyId = userContext?.companyID;
  const dateCreated = userContext?.dateCreated;

  // Show "Onboarding Support" only during the first
  // ONBOARDING_SUPPORT_ELIGIBLE_DAYS after business creation.
  const businessAgeMs = dateCreated
    ? Date.now() - new Date(dateCreated).getTime()
    : Number.POSITIVE_INFINITY;
  const isEligibleForOnboardingSupport =
    !!dateCreated &&
    businessAgeMs < ONBOARDING_SUPPORT_ELIGIBLE_DAYS * 24 * 60 * 60 * 1000;

  const [isOpen, setOpen] = useState<boolean>(false);
  const [showOnboardingOption, setShowOnboardingOption] =
    useState<boolean>(false);
  const [previousCompanyId, setPreviousCompanyId] = useState<
    string | undefined
  >(undefined);

  // Check onboarding card state and update menu visibility
  // This runs on all pages, not just the dashboard
  useEffect(() => {
    if (!companyId) {
      setShowOnboardingOption(false);
      return;
    }

    const isDashboard = location.pathname === '/app/reports/dashboard';

    // When company changes, fetch from backend and update localStorage
    if (companyId !== previousCompanyId) {
      setPreviousCompanyId(companyId);

      // Fetch from backend to get the latest state for the new company
      const fetchCardState = async () => {
        try {
          const state = await getCompanyOnboardingState(companyId);
          if (state) {
            // Update localStorage with the backend state
            setOnboardingCardStateInStorage(companyId, state.cardState);
            // Show option if card is closed
            setShowOnboardingOption(state.cardState === 'closed');
          }
        } catch (error) {
          console.error(
            'Error fetching onboarding state on company switch:',
            error
          );
          setShowOnboardingOption(false);
        }
      };

      fetchCardState();
      return;
    }

    // On dashboard, always fetch from backend to ensure it's up to date
    if (isDashboard) {
      const fetchCardState = async () => {
        try {
          const state = await getCompanyOnboardingState(companyId);
          if (state) {
            // Update localStorage with the backend state
            setOnboardingCardStateInStorage(companyId, state.cardState);
            // Show option if card is closed
            setShowOnboardingOption(state.cardState === 'closed');
          }
        } catch (error) {
          console.error('Error fetching onboarding state on dashboard:', error);
          setShowOnboardingOption(false);
        }
      };

      fetchCardState();
    } else {
      // On non-dashboard pages, use localStorage for quick access
      const storedState = getOnboardingCardStateFromStorage(companyId);
      if (storedState) {
        setShowOnboardingOption(storedState === 'closed');
      } else {
        // If no localStorage value, don't show the option
        // (it will be populated when they visit dashboard)
        setShowOnboardingOption(false);
      }
    }

    // Listen for storage changes from other components
    const handleStorageChange = (event: any) => {
      if (event.detail?.companyId === companyId) {
        setShowOnboardingOption(event.detail.cardState === 'closed');
      }
    };

    window.addEventListener(
      'onboardingCardStateStorageChanged',
      handleStorageChange
    );

    // Listen for custom events when onboarding card state changes (legacy support)
    const handleOnboardingStateChange = (event: any) => {
      if (event.detail) {
        // Show option when card is hidden (visible: false)
        setShowOnboardingOption(!event.detail.visible);
      }
    };

    window.addEventListener(
      'onboardingCardStateChanged',
      handleOnboardingStateChange
    );

    return () => {
      window.removeEventListener(
        'onboardingCardStateStorageChanged',
        handleStorageChange
      );
      window.removeEventListener(
        'onboardingCardStateChanged',
        handleOnboardingStateChange
      );
    };
  }, [location.pathname, companyId, previousCompanyId]);

  // Let other surfaces (e.g. the dashboard help card) open this same modal.
  useEffect(() => {
    const handleOpenRequest = (): void => setOpen(true);
    window.addEventListener(OPEN_SUPPORT_MODAL_EVENT, handleOpenRequest);
    return () => {
      window.removeEventListener(OPEN_SUPPORT_MODAL_EVENT, handleOpenRequest);
    };
  }, []);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleReopenOnboarding = (): void => {
    handleClose();

    const isDashboard = location.pathname === '/app/reports/dashboard';

    if (isDashboard) {
      // Already on dashboard - just show the card
      if ((window as any).showOnboardingCard) {
        (window as any).showOnboardingCard();
      }
    } else {
      // Not on dashboard - navigate to dashboard with query parameter to open the card
      history.push('/app/reports/dashboard?openOnboarding=true');
    }
  };

  return (
    <Box>
      <HtmlTooltip title="Help & Support">
        <IconButton color="inherit" onClick={handleOpen} size="large">
          <SvgIcon>
            <HelpCircle />
          </SvgIcon>
        </IconButton>
      </HtmlTooltip>
      <SupportModal
        open={isOpen}
        onClose={handleClose}
        showOnboardingSupport={isEligibleForOnboardingSupport}
        showOnboardingGuide={showOnboardingOption}
        onReopenOnboarding={handleReopenOnboarding}
      />
    </Box>
  );
};

export default Contact;
